<template>
<div class="st-page">
  <Navigation />
  <PhotoLightbox :src="lightbox.src" :caption="lightbox.caption" @close="closeLightbox" />
  <div class="container" style="padding-top:20px;max-width:1300px;">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/students')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <div>
          <h4 class="st-page-title"><i class="mdi mdi-account-multiple me-2"></i>All Parents & Guardians</h4>
          <p style="color:#8BA4C4;font-size:12px;margin:0;">Every parent record across all students. Filter, search, and message in bulk.</p>
        </div>
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <button class="st-btn st-btn-primary" @click="openCompose(null)" :disabled="!parents.length"><i class="mdi mdi-email-fast me-1"></i>Send Custom SMS</button>
        <button class="st-btn st-btn-blue" @click="$router.push('/sms')"><i class="mdi mdi-message-text-fast me-1"></i>Bulk SMS</button>
        <button class="st-btn st-btn-blue" @click="resendAll" :disabled="resendingBulk">
          <span v-if="resendingBulk" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-send-circle me-1"></i>{{ resendingBulk ? 'Sending...' : 'Resend Welcome (All)' }}
        </button>
      </div>
    </div>

    <!-- Stats -->
    <div class="st-stat-row">
      <div class="st-stat"><div class="st-stat-num">{{ stats.total || 0 }}</div><div class="st-stat-lbl">Total parent records</div></div>
      <div class="st-stat"><div class="st-stat-num">{{ stats.unique_phones || 0 }}</div><div class="st-stat-lbl">Unique phone numbers</div></div>
      <div class="st-stat"><div class="st-stat-num" style="color:#10B981;">{{ stats.sms_on || 0 }}</div><div class="st-stat-lbl">SMS opt-in</div></div>
      <div class="st-stat"><div class="st-stat-num" style="color:#4FC3F7;">{{ stats.with_email || 0 }}</div><div class="st-stat-lbl">With email</div></div>
    </div>

    <!-- Filters -->
    <div class="st-card mb-3">
      <div class="row g-2 align-items-end">
        <div class="col-md-4">
          <label class="st-label">Search</label>
          <input class="st-input" v-model="filters.search" placeholder="Name, phone, email, student name or admission no" @input="debounceLoad" />
        </div>
        <div class="col-md-3">
          <label class="st-label">Class</label>
          <select class="st-select" v-model="filters.classId" @change="loadData">
            <option value="">All</option>
            <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="st-label">Relationship</label>
          <select class="st-select" v-model="filters.relationship" @change="loadData">
            <option value="">Any</option>
            <option value="father">Father</option>
            <option value="mother">Mother</option>
            <option value="guardian">Guardian</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="st-label">SMS</label>
          <select class="st-select" v-model="filters.smsFilter" @change="loadData">
            <option value="">All</option>
            <option value="on">SMS On</option>
            <option value="off">SMS Off</option>
          </select>
        </div>
        <div class="col-md-1">
          <button class="st-btn st-btn-blue w-100" @click="loadData" :disabled="loading"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
    <div v-else-if="!parents.length" class="st-empty"><i class="mdi mdi-account-off"></i>No parents match these filters</div>
    <div v-else class="st-card">
      <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <span style="color:#8BA4C4;font-size:13px;">{{ parents.length }} of {{ stats.total }} shown</span>
        <div class="d-flex gap-2">
          <button class="st-btn st-btn-sm" style="background:rgba(46,134,222,.2);color:#4FC3F7;border:1px solid rgba(46,134,222,.4);" @click="openCompose(null)"><i class="mdi mdi-email-fast me-1"></i>Send to filtered ({{ parents.length }})</button>
          <button class="st-btn st-btn-sm st-btn-blue" @click="exportCsv"><i class="mdi mdi-download me-1"></i>Export CSV</button>
        </div>
      </div>
      <div style="overflow-x:auto;">
        <table class="st-table">
          <thead>
            <tr>
              <th style="width:60px;">Photo</th>
              <th>Parent</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Relationship</th>
              <th>Student</th>
              <th>Class</th>
              <th>Notify</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in parents" :key="p.id">
              <td>
                <div class="pl-avatar" @click="p.student_photo_url && openLightbox(p)" :title="p.student_photo_url ? 'Click to enlarge' : p.student_name" :style="{cursor: p.student_photo_url ? 'pointer' : 'default'}">
                  <img v-if="p.student_photo_url" :src="p.student_photo_url" :alt="p.student_name" loading="lazy" />
                  <span v-else class="pl-avatar-fallback">{{ avatarInitials(p.student_name) }}</span>
                </div>
              </td>
              <td style="font-weight:600;">
                {{ p.parent_name }}
                <span v-if="p.is_primary == 1" class="st-badge st-badge-info" style="font-size:10px;margin-left:4px;">Primary</span>
              </td>
              <td>{{ p.phone || '—' }}</td>
              <td style="color:#8BA4C4;">{{ p.email || '—' }}</td>
              <td style="text-transform:capitalize;">{{ p.relationship }}</td>
              <td>
                <a href="javascript:void(0)" @click="$router.push('/students')" style="color:#4FC3F7;text-decoration:none;">{{ p.student_name }}</a>
                <div style="color:#8BA4C4;font-size:11px;">{{ p.admission_no }}</div>
              </td>
              <td>{{ p.class_name || '—' }}</td>
              <td>
                <span class="st-badge" :class="p.receives_sms == 1 ? 'st-badge-success' : 'st-badge-danger'" style="font-size:10px;">
                  {{ p.receives_sms == 1 ? 'SMS' : 'no SMS' }}
                </span>
              </td>
              <td style="white-space:nowrap;">
                <button class="st-btn st-btn-sm me-1" style="background:rgba(46,134,222,.2);color:#4FC3F7;border:1px solid rgba(46,134,222,.4);" @click="openCompose(p)" title="Send custom SMS to this parent"><i class="mdi mdi-email-fast"></i></button>
                <button class="st-btn st-btn-sm st-btn-blue me-1" @click="resendOne(p)" :disabled="resendingId === p.id" title="Resend welcome SMS">
                  <i v-if="resendingId !== p.id" class="mdi mdi-send"></i>
                  <span v-else class="spinner-border spinner-border-sm"></span>
                </button>
                <a v-if="p.phone" :href="'tel:' + p.phone" class="st-btn st-btn-sm st-btn-green me-1" title="Call"><i class="mdi mdi-phone"></i></a>
                <a v-if="p.phone" :href="'https://wa.me/' + p.phone.replace(/[^0-9]/g, '')" target="_blank" class="st-btn st-btn-sm" style="background:rgba(37,211,102,.15);color:#25D366;border:1px solid rgba(37,211,102,.3);" title="WhatsApp">
                  <i class="mdi mdi-whatsapp"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ============ COMPOSE SMS MODAL ============ -->
    <div class="st-modal-overlay" v-if="showCompose" @click.self="closeCompose">
      <div class="st-modal" style="max-width:560px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-email-fast me-2"></i>{{ composeStage === 'confirm' ? 'Confirm Send' : 'Send Custom SMS' }}</span>
          <button class="st-modal-close" @click="closeCompose">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="composeError">{{ composeError }}</div>
        <div class="st-alert st-alert-success" v-if="composeSuccess">{{ composeSuccess }}</div>

        <!-- ===== STAGE 1: COMPOSE ===== -->
        <div v-if="composeStage === 'compose'">
          <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px 14px;margin-bottom:14px;">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
              <div>
                <div style="color:#E8EDF4;font-weight:700;font-size:13px;">{{ composeRecipientLabel }}</div>
                <div style="color:#8BA4C4;font-size:11px;margin-top:2px;">{{ composeRecipientCount }} parent{{ composeRecipientCount === 1 ? '' : 's' }} will receive this SMS</div>
              </div>
              <div style="color:#F59E0B;font-size:11px;font-weight:700;">{{ composeRecipientCount }} credits</div>
            </div>
          </div>

          <div class="mb-3">
            <label class="st-label">Title <small style="font-weight:400;color:#8BA4C4;">(optional, internal label)</small></label>
            <input class="st-input" v-model="compose.title" placeholder="e.g. AGM 14 May 2026" />
          </div>
          <div class="mb-3">
            <label class="st-label">Message *</label>
            <textarea class="st-textarea" v-model="compose.message" rows="5" placeholder="e.g. Dear Parent, you are invited to the AGM on Saturday 14 May 2026 at 10am at the school hall. Tea will be served. — Simi Trac"></textarea>
            <div style="display:flex;justify-content:space-between;color:#8BA4C4;font-size:11px;margin-top:4px;">
              <span>{{ compose.message.length }} chars</span>
              <span>{{ Math.max(1, Math.ceil(compose.message.length / 160)) }} SMS unit{{ Math.ceil(compose.message.length / 160) === 1 ? '' : 's' }} per recipient</span>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button class="btn st-btn-primary flex-fill" @click="goToConfirm" :disabled="!compose.message || !composeRecipientCount"><i class="mdi mdi-arrow-right-thick me-1"></i>Review & Send</button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeCompose">Cancel</button>
          </div>
        </div>

        <!-- ===== STAGE 2: CONFIRM (type SEND) ===== -->
        <div v-else-if="composeStage === 'confirm'">
          <div style="background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.3);border-radius:10px;padding:14px;margin-bottom:14px;">
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
              <i class="mdi mdi-alert" style="color:#F59E0B;font-size:24px;"></i>
              <div>
                <div style="color:#E8EDF4;font-weight:700;">Final review — this is irreversible</div>
                <div style="color:#8BA4C4;font-size:11px;">{{ composeRecipientCount }} parent{{ composeRecipientCount === 1 ? '' : 's' }} · {{ composeCreditsTotal }} SMS credits</div>
              </div>
            </div>
          </div>

          <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px 14px;margin-bottom:14px;">
            <div style="font-size:10px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin-bottom:6px;">Recipients</div>
            <div style="color:#E8EDF4;font-size:13px;font-weight:600;">{{ composeRecipientLabel }}</div>
            <div v-if="compose.title" style="margin-top:8px;font-size:10px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;">Title</div>
            <div v-if="compose.title" style="color:#E8EDF4;font-size:13px;">{{ compose.title }}</div>
            <div style="margin-top:8px;font-size:10px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;">Message preview</div>
            <div style="color:#E8EDF4;font-size:13px;white-space:pre-wrap;background:#06101F;border-radius:6px;padding:10px;margin-top:4px;">{{ compose.message }}</div>
          </div>

          <div class="mb-3">
            <label class="st-label">Type <strong style="color:#F59E0B;">SEND</strong> in capital letters to confirm</label>
            <input class="st-input confirm-input" v-model="confirmText" placeholder="SEND" autocomplete="off" autocorrect="off" autocapitalize="characters" spellcheck="false" @keyup.enter="confirmText === 'SEND' && sendCompose()" />
            <small v-if="confirmText && confirmText !== 'SEND'" style="color:#EF4444;font-size:11px;">Must match exactly: SEND</small>
          </div>

          <div class="d-flex gap-2">
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="composeStage = 'compose'; confirmText = ''"><i class="mdi mdi-arrow-left me-1"></i>Edit</button>
            <button class="btn st-btn-primary flex-fill" @click="startCountdown" :disabled="confirmText !== 'SEND'">
              <i class="mdi mdi-timer-sand me-1"></i>Schedule Send (40s timer)
            </button>
          </div>
        </div>

        <!-- ===== STAGE 3: COUNTDOWN ===== -->
        <div v-else-if="composeStage === 'countdown'">
          <div style="text-align:center;padding:14px 0 8px;">
            <div class="countdown-ring" :class="{urgent: countdownSec <= 10}">
              <div class="countdown-num">{{ countdownSec }}</div>
              <div class="countdown-lbl">seconds</div>
            </div>
            <div style="color:#E8EDF4;font-weight:700;margin-top:14px;font-size:14px;">SMS will fire when the timer hits zero</div>
            <div style="color:#8BA4C4;font-size:12px;margin-top:4px;">Use this window to spot a typo — hit Cancel to abort.</div>
          </div>

          <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px 14px;margin:14px 0;">
            <div style="font-size:10px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin-bottom:6px;">Going to {{ composeRecipientCount }} parent{{ composeRecipientCount === 1 ? '' : 's' }} · {{ composeCreditsTotal }} credits</div>
            <div style="color:#E8EDF4;font-size:13px;font-weight:600;">{{ composeRecipientLabel }}</div>
            <div style="margin-top:8px;font-size:10px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;">Message</div>
            <div style="color:#E8EDF4;font-size:13px;white-space:pre-wrap;background:#06101F;border-radius:6px;padding:10px;margin-top:4px;max-height:160px;overflow:auto;">{{ compose.message }}</div>
          </div>

          <div class="d-flex gap-2">
            <button class="btn flex-fill" style="background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.4);border-radius:10px;padding:14px 18px;font-weight:800;font-size:15px;" @click="cancelCountdown">
              <i class="mdi mdi-cancel me-1"></i>Cancel Send
            </button>
            <button class="btn" style="background:linear-gradient(135deg,#10B981,#059669);color:#fff;border:none;border-radius:10px;padding:14px 18px;font-weight:700;" @click="sendNow" title="Skip the timer and send immediately">
              <i class="mdi mdi-send me-1"></i>Send Now
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import PhotoLightbox from "@/components/simi-students/PhotoLightbox.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "ParentsList",
  components: { Navigation, PhotoLightbox },
  data() {
    return {
      parents: [], stats: {},
      classes: [],
      loading: true,
      lightbox: { src: '', caption: '' },
      filters: { search: '', classId: '', relationship: '', smsFilter: '' },
      resendingId: null,
      resendingBulk: false,
      _searchTimer: null,
      // Compose SMS
      showCompose: false,
      composeStage: 'compose',     // 'compose' | 'confirm' | 'countdown'
      composeTarget: null,           // null = all filtered; otherwise a single parent record
      compose: { title: '', message: '' },
      composeSending: false,
      composeError: '',
      composeSuccess: '',
      confirmText: '',
      countdownSec: 40,
      countdownHandle: null,
    };
  },
  beforeUnmount() {
    if (this.countdownHandle) clearInterval(this.countdownHandle);
  },
  computed: {
    composeRecipientLabel() {
      if (this.composeTarget) return `${this.composeTarget.parent_name} (${this.composeTarget.phone || 'no phone'})`;
      const fp = [];
      if (this.filters.classId) fp.push('class filter');
      if (this.filters.relationship) fp.push(this.filters.relationship);
      if (this.filters.smsFilter) fp.push('SMS ' + this.filters.smsFilter);
      if (this.filters.search) fp.push('search: ' + this.filters.search);
      return fp.length ? `Filtered parents (${fp.join(' · ')})` : 'All parents on this page';
    },
    composeRecipientCount() {
      if (this.composeTarget) return this.composeTarget.phone ? 1 : 0;
      return this.parents.filter(p => !!p.phone).length;
    },
    composeCreditsTotal() {
      const units = Math.max(1, Math.ceil((this.compose.message || '').length / 160));
      return this.composeRecipientCount * units;
    },
  },
  mounted() { this.loadClasses(); this.loadData(); },
  methods: {
    avatarInitials(name) {
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    openLightbox(p) {
      if (!p || !p.student_photo_url) return;
      this.lightbox = { src: p.student_photo_url, caption: `${p.student_name} · ${p.admission_no}` + (p.class_name ? ` · ${p.class_name}` : '') };
    },
    closeLightbox() { this.lightbox = { src: '', caption: '' }; },
    debounceLoad() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.loadData(), 350);
    },
    async loadClasses() {
      try {
        const r = await executeJson({ function: 'getClasses' }, simitrack);
        if (r.data.success) this.classes = Array.isArray(r.data.data) ? r.data.data : (r.data.data.classes || []);
      } catch (e) {}
    },
    async loadData() {
      this.loading = true;
      try {
        const r = await executeJson({ function: 'getAllParents', ...this.filters }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.parents = d.parents || [];
          this.stats = d.stats || {};
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    async resendOne(p) {
      this.resendingId = p.id;
      try {
        const r = await executeJson({ function: 'resendParentWelcome', parentRecordId: p.id }, simitrack);
        if (r.data.success) alert('✓ ' + (r.data.message || 'Welcome SMS resent.'));
        else alert('✗ ' + (r.data.message || 'Failed'));
      } catch (e) { alert('Connection error'); }
      this.resendingId = null;
    },
    async resendAll() {
      if (!confirm('Resend the welcome SMS (with parent-portal link) to EVERY parent in this school? This consumes SMS credits.')) return;
      this.resendingBulk = true;
      try {
        const r = await executeJson({ function: 'resendParentWelcome' }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          alert('✓ Resent welcome SMS to ' + (d.sent || 0) + ' parent(s)' + (d.failed ? ', ' + d.failed + ' failed' : '') + '.');
        } else alert('✗ ' + (r.data.message || 'Failed'));
      } catch (e) { alert('Connection error'); }
      this.resendingBulk = false;
    },
    openCompose(parent) {
      this.composeTarget = parent || null;
      this.composeStage = 'compose';
      this.compose = { title: '', message: '' };
      this.confirmText = '';
      this.composeError = '';
      this.composeSuccess = '';
      this.showCompose = true;
    },
    closeCompose() {
      this.stopCountdown();
      this.showCompose = false;
      this.composeTarget = null;
      this.composeStage = 'compose';
      this.confirmText = '';
    },
    goToConfirm() {
      this.composeError = '';
      if (!this.compose.message || this.compose.message.trim().length < 2) { this.composeError = 'Message is required'; return; }
      if (!this.composeRecipientCount) { this.composeError = 'No recipients with phone numbers'; return; }
      this.confirmText = '';
      this.composeStage = 'confirm';
    },
    startCountdown() {
      if (this.confirmText !== 'SEND') { this.composeError = 'Please type SEND in capitals first'; return; }
      this.composeError = '';
      this.composeStage = 'countdown';
      this.countdownSec = 40;
      this.stopCountdown();
      this.countdownHandle = setInterval(() => {
        this.countdownSec -= 1;
        if (this.countdownSec <= 0) {
          this.stopCountdown();
          this.performSend();
        }
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownHandle) { clearInterval(this.countdownHandle); this.countdownHandle = null; }
    },
    cancelCountdown() {
      this.stopCountdown();
      this.composeStage = 'confirm';
      this.confirmText = '';
      this.composeSuccess = '';
      this.composeError = 'Send cancelled.';
      setTimeout(() => { this.composeError = ''; }, 3000);
    },
    sendNow() {
      this.stopCountdown();
      this.performSend();
    },
    async performSend() {
      this.composeSending = true; this.composeError = ''; this.composeSuccess = '';
      try {
        let parentIds;
        if (this.composeTarget) {
          parentIds = [this.composeTarget.id];
        } else {
          parentIds = this.parents.filter(p => !!p.phone).map(p => p.id);
        }
        const params = {
          function: 'sendBulkSMS',
          targetType: 'specific_parents',
          parentIds: JSON.stringify(parentIds),
          message: this.compose.message,
          title: this.compose.title || '',
        };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.composeSuccess = `✓ Sent: ${d.sent || 0} delivered${d.failed ? ', ' + d.failed + ' failed' : ''}.`;
          setTimeout(() => this.closeCompose(), 2200);
        } else {
          this.composeError = r.data.message || 'Send failed';
          this.composeStage = 'confirm';
        }
      } catch (e) { this.composeError = 'Connection error'; this.composeStage = 'confirm'; }
      this.composeSending = false;
    },

    exportCsv() {
      const cols = ['Parent','Phone','Email','Relationship','Primary','SMS','Email opt-in','Student','Admission No','Class'];
      const lines = [cols.join(',')];
      for (const p of this.parents) {
        const fields = [
          p.parent_name, p.phone || '', p.email || '', p.relationship,
          p.is_primary == 1 ? 'Yes' : '', p.receives_sms == 1 ? 'Yes' : 'No', p.receives_email == 1 ? 'Yes' : 'No',
          p.student_name, p.admission_no, p.class_name || '',
        ].map(v => '"' + String(v || '').replace(/"/g, '""') + '"');
        lines.push(fields.join(','));
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'parents-' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click(); URL.revokeObjectURL(url);
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:20px;margin-bottom:16px;}
.st-stat-row{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:14px;}
.st-stat{background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;padding:14px 18px;flex:1;min-width:160px;}
.st-stat-num{font-size:24px;font-weight:800;color:#4FC3F7;}
.st-stat-lbl{font-size:11px;color:#8BA4C4;margin-top:2px;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;white-space:nowrap;}
.st-table td{color:#E8EDF4;font-size:13px;padding:10px 12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.04);}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;}
.st-btn{border:none;border-radius:8px;padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;}
.st-btn-sm{padding:5px 10px;font-size:12px;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-green{background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.3);}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;}
.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-label{color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;display:block;}
.st-input,.st-select,.st-textarea{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;}
.st-input:focus,.st-select:focus,.st-textarea:focus{border-color:#2E86DE;}
.st-textarea{min-height:100px;resize:vertical;font-family:inherit;line-height:1.5;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
.st-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:16px;padding:28px;width:100%;max-height:90vh;overflow-y:auto;}
.st-modal-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.st-modal-close{color:#8BA4C4;cursor:pointer;font-size:22px;background:none;border:none;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:14px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#10B981;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE 0%,#4FC3F7 100%);color:#fff;border:none;border-radius:10px;padding:10px 22px;font-weight:700;}
.confirm-input{font-family:'Courier New',monospace;letter-spacing:6px;text-align:center;font-size:18px;font-weight:800;text-transform:uppercase;}
.confirm-input:focus{border-color:#F59E0B;}
.countdown-ring{display:inline-flex;flex-direction:column;align-items:center;justify-content:center;width:140px;height:140px;border-radius:50%;border:6px solid rgba(79,195,247,.4);background:rgba(46,134,222,.08);transition:border-color .3s,background .3s;}
.countdown-ring.urgent{border-color:rgba(239,68,68,.5);background:rgba(239,68,68,.08);animation:pulse 1s infinite;}
.countdown-num{font-size:48px;font-weight:900;color:#4FC3F7;line-height:1;}
.countdown-ring.urgent .countdown-num{color:#EF4444;}
.countdown-lbl{font-size:11px;color:#8BA4C4;text-transform:uppercase;letter-spacing:2px;margin-top:4px;}
@keyframes pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.04);}}

/* Student photo thumbnail in the parents row */
.pl-avatar{width:40px;height:48px;border-radius:6px;overflow:hidden;background:#0F2347;border:1px solid #1A3A6A;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.pl-avatar img{width:100%;height:100%;object-fit:cover;display:block;}
.pl-avatar-fallback{color:#4FC3F7;font-weight:700;font-size:13px;letter-spacing:.5px;}
</style>
